import { Link } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import useAuth from "@/hooks/use-auth";
import { cn } from "@/lib/utils";

import { GameSortOptionsEnum } from "../../types";
import type { SortOptionsType } from "./constants";
import { SelectedIcon } from "./icons";

interface DesktopMenuProps {
  href: string;
  isCurrentUser: boolean;
  onClick(): void;
  onSignup(): void;
  options: SortOptionsType;
  sortBy: GameSortOptionsEnum;
}

export default function DesktopMenu({
  href,
  isCurrentUser,
  onClick,
  onSignup,
  options,
  sortBy,
}: DesktopMenuProps) {
  const { isUser } = useAuth();

  return (
    <div className="flex flex-col gap-2.5 rounded-[15px] bg-space-gray-dark p-2.5 shadow-0-0-8 shadow-black/25">
      {Object.entries(options).map(([key, { icon, name }]) => {
        if (isCurrentUser && key === GameSortOptionsEnum.USER_RATING) {
          return null;
        }

        const buttonProps: React.ComponentProps<typeof Button> = { onClick };

        let children = (
          <>
            <span className="mr-2.5 flex w-4.5 justify-center">{icon}</span>
            {name}
            {sortBy === key && <SelectedIcon className="ml-auto" />}
          </>
        );

        if (key === GameSortOptionsEnum.MY_RATING && !isUser) {
          buttonProps.onClick = () => {
            onClick();
            onSignup();
          };
        } else {
          buttonProps.asChild = true;
          children = (
            <Link replace to={href + key}>
              {children}
            </Link>
          );
        }

        return (
          <Button
            className={cn(
              "flex h-10 items-center rounded-[10px] px-3 py-1.5 text-14 text-white transition-colors hover:bg-white/10",
              'after:ml-auto after:size-5 after:rounded-full after:border after:border-white/30 after:content-[""]',
              sortBy === key ? "after:hidden" : "after:block"
            )}
            key={key}
            {...buttonProps}
          >
            {children}
          </Button>
        );
      })}
    </div>
  );
}
